import { ArchivedPartialReplayCoordinator, type ArchivedReplayCommit, type ArchivedReplayLocalState, type ReplayRow } from './ArchivedPartialReplayCoordinator';
import { readArchivedPartialReplayRemote } from './ArchivedPartialReplayRemoteReader';

export const prepareArchivedPartialReplays = async (input: {
  companyId: string;
  clientBuild: number;
  client: Parameters<typeof readArchivedPartialReplayRemote>[0]['client'];
  database: {
    listItems<T>(table: string, query?: any): Promise<T[]>;
    readItem<T>(table: string, id: string): Promise<T | null>;
    commitArchivedPartialReplay(commit: ArchivedReplayCommit): Promise<boolean>;
  };
  assertSession(): Promise<void>;
  isSessionCurrent(): boolean;
  onPreserved?(reason: string): void;
}): Promise<void> => {
  if (input.clientBuild < 124) return;
  const db = input.database;
  await input.assertSession();
  const archives = await db.listItems<ReplayRow>('accounted_pending_operations', {
    empresa_id: input.companyId, accounting_state: 'conflict_materialized',
  });
  if (!Array.isArray(archives)) return;
  const service = new ArchivedPartialReplayCoordinator({
    assertSession: input.assertSession,
    isSessionCurrent: input.isSessionCurrent,
    now: Date.now,
    async readLocal(archiveId, companyId) {
      const archive = await db.readItem<ArchivedReplayLocalState['archive']>('accounted_pending_operations', archiveId);
      if (!archive || archive.empresa_id !== companyId) return null;
      const checklist = await db.readItem<ReplayRow>('guincho_checklists', archive.item_id);
      if (!checklist || checklist.empresa_id !== companyId) return null;
      const conflicts = await db.listItems<ReplayRow>('sync_conflicts', { empresa_id: companyId, item_id: archive.item_id });
      const conflict = conflicts.find(row => row.id === archive.id.replace(/^accounted:/, 'conflict:'));
      if (!conflict || !['open', 'blocked'].includes(conflict.status)) return null;
      const values = await db.listItems<ArchivedReplayLocalState['values'][number]>('guincho_valores_checklist', {
        empresa_id: companyId, checklist_id: archive.item_id,
      });
      // Inclui pendência legada com empresa NULL: também é trabalho concorrente a preservar.
      const pending = await db.listItems<ReplayRow>('pending_operations', { item_id: archive.item_id });
      return { archive, conflict: conflict as ArchivedReplayLocalState['conflict'], checklist, values, conflicts, pending };
    },
    readRemote: (local, companyId) => readArchivedPartialReplayRemote({
      local, companyId, client: input.client, clientBuild: input.clientBuild, assertSession: input.assertSession,
    }),
    commit: commit => db.commitArchivedPartialReplay(commit),
  });
  for (const archive of archives) {
    if (archive.operation !== 'checklist_partial_update' || archive.collection !== 'guincho_valores_checklist' ||
      !['tombstoned_entity', 'field_reference_not_resolved'].includes(archive.reason)) continue;
    try {
      await service.process(archive.id, input.companyId);
    } catch (error) {
      await input.assertSession();
      input.onPreserved?.(error instanceof Error ? error.message : 'archived_replay_preserved');
    }
  }
};
